import { BenefitType, ReferentPriceType, USState } from '@strata/constants';
import { capitalizeString } from '@strata/helper-functions';
import { CurrentBid } from '@strata/react-current-bid';

import { useAppSelector } from 'store';
import { ReferralValueCase } from 'store/api/performanceApi';

export interface LiveReferral {
    referralId: string;
    payerId: string;
    segmentId: string;
    productServiceOid: string;
    product: string;
    healthPlan: string;
    segmentName: string;
    referentPriceType: ReferentPriceType;
    strength: string;
    qty: number;
    refillCount: number;
    incentiveTotal?: number;
    auctionEnd: string;
    memberAge: number;
    memberCity: string;
    memberState: USState;
    memberZip: string;
    diagnosisName: string;
    prescriberId: string;
    prescriberName: string;
    placeOfService: string;
    referralExpiration: string;
    valueCases: ReferralValueCase[];
    currentBidAmt: string;
    currentBidState: (typeof CurrentBid.states)[keyof typeof CurrentBid.states];
    maxBid?: number;
    tied: boolean;
    benefit: BenefitType;
    isBrand: boolean;
    priceSource: string;
    priceIsFrozen: boolean;
}

export const useLiveReferralData = (): {
    liveReferralDataLoaded: boolean;
    liveReferralData: LiveReferral[];
} => {
    const { openAuctions, winningBids, liveReferralDataLoaded } = useAppSelector(
        (state) => state.liveReferrals,
    );
    return {
        liveReferralDataLoaded,
        liveReferralData: openAuctions.map((auction) => ({
            referralId: auction.referral_id,
            payerId: auction.payer_id,
            segmentId: auction.segment_id,
            productServiceOid: auction.product_service_oid,
            product: auction.product_service_description,
            healthPlan: auction.health_plan,
            segmentName: auction.segment_name,
            referentPriceType: auction.referent_price_type,
            strength: auction.strength,
            qty: Number(auction.product_qty),
            refillCount: auction.rx_refillcount,
            incentiveTotal: auction.incentive_total,
            auctionEnd: auction.auction_end,
            memberAge: auction.member_age,
            memberCity: auction.member_city,
            memberState: auction.member_state,
            memberZip: auction.member_zip,
            diagnosisName: auction.dx_name,
            prescriberId: auction.prescriber_id,
            prescriberName: capitalizeString(auction.prescriber_name),
            placeOfService: auction.place_of_service?.trim(),
            referralExpiration: auction.referral_expiration,
            valueCases: auction.value_cases,
            ...(winningBids[auction.referral_id] ?? {
                currentBidAmt: auction.default_rate,
                currentBidState: CurrentBid.states.NO_BID,
            }),
            benefit: auction.benefit?.trim() as BenefitType,
            isBrand: auction.is_brand,
            priceSource: auction.price_source,
            priceIsFrozen: auction.price_is_frozen,
        })),
    };
};
